import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getAllCompanyUsers,
  getAllCompanyUsersStatus,
  getAllCompanyUsersError,
} from '../store/user.selectors';
import { selectUserForEdit } from '../store/user.actions';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  '&:before': {
    display: 'none',
  },
  marginBottom: theme.spacing(1),
  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px',
});

const UsersList = ({ handleOpenModal }) => {
  const dispatch = useDispatch();

  const users = useSelector(getAllCompanyUsers);
  const status = useSelector(getAllCompanyUsersStatus);
  const error = useSelector(getAllCompanyUsersError);

  const handleEdit = (user) => (event) => {
    dispatch(selectUserForEdit(user));
    handleOpenModal();
    event.stopPropagation();
  };

  const handleCloseError = () => {};

  if (status === 'loading') {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: 'lg',
        mx: 'auto',
        p: 3,
        maxHeight: '75vh',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          background: alpha('#000', 0.05),
          borderRadius: 8,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: alpha('#000', 0.2),
          borderRadius: 8,
          '&:hover': {
            backgroundColor: alpha('#000', 0.3),
          },
        },
      }}
    >
      <Snackbar
        open={status === 'fail'}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error || 'Failed to load users'}
        </Alert>
      </Snackbar>
      {status === 'success' && Object.entries(users).length > 0 ? (
        Object.entries(users).map(([companyName, users]) => (
          <StyledAccordion key={companyName}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <Typography sx={{ fontWeight: 500 }}>
                {companyName} ({users.length})
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 0 }}>
              <List disablePadding>
                {users.map((user) => (
                  <StyledListItem
                    key={user.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 2,
                      px: 3,
                    }}
                  >
                    <Avatar
                      src={
                        user.profilePictureFileRefID
                          ? `/api/files/${user.profilePictureFileRefID}`
                          : undefined
                      }
                      sx={{ mr: 2 }}
                    >
                      {user.name.charAt(0)}
                    </Avatar>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontWeight: 500 }}>{user.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.roleID} | {user.email}
                      </Typography>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={handleEdit(user)}
                      sx={{
                        color: 'action.active',
                        '&:hover': {
                          color: 'text.primary',
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </StyledListItem>
                ))}
              </List>
            </AccordionDetails>
          </StyledAccordion>
        ))
      ) : status === 'success' ? (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
          No users found
        </Typography>
      ) : null}
    </Box>
  );
};

export default UsersList;

UsersList.propTypes = {
  handleOpenModal: PropTypes.func.isRequired,
};
